/**
 * Permission presets — quick-start bundles for the role editor.
 *
 * Each preset is scoped to one or more apps (matching `useAuthStore().app`) and
 * declares the (entity, action) pairs it grants. The role dialog renders the
 * preset chips above the dual-list; clicking a chip unions its permissions
 * into the current selection (never replaces), so managers can stack presets.
 *
 * All presets include CoCompany/CoBrand read so the Home loads correctly —
 * without it the member 403s on the workspace landing.
 */

import type { Action } from './permissionCatalog';
import type { PermissionItem } from './components/PermissionDualList';

const READ: Action[] = ['show', 'list'];
const WRITE: Action[] = ['show', 'list', 'edit', 'create', 'delete', 'full'];
const WRITE_DEPLOY: Action[] = ['show', 'list', 'edit', 'create', 'delete', 'full', 'deploy'];

const CO_READ = ['CoCompany', 'CoBrand'];

const XK_ALL = [
  'XkCompany',
  'XkStock',
  'XkEvent',
  'XkEventGroup',
  'XkStation',
  'XkEventStockStation',
  'XkDashboard',
];
const XP_ALL = ['XpProject', 'XpEvents', 'XpEventGroup', 'XpStation', 'XpReporter'];

function expand(entities: string[], actions: Action[]): PermissionItem[] {
  return entities.flatMap((entity) => actions.map((action) => ({ entity, action })));
}

export interface PermissionPreset {
  id: string;
  label: string;
  description: string;
  /** Apps this preset is visible in. */
  apps: string[];
  permissions: PermissionItem[];
}

export const PERMISSION_PRESETS: PermissionPreset[] = [
  // ─── Xotski ──────────────────────────────────────────────────────────────
  {
    id: 'xk-viewer',
    label: 'Viewer (read-only)',
    description: 'See everything in Xotski. No edits.',
    apps: ['xotski'],
    permissions: [...expand(CO_READ, READ), ...expand(XK_ALL, READ)],
  },
  {
    id: 'xk-inventory-manager',
    label: 'Inventory manager',
    description: 'Full control over stock and station allocation. Read events, groups, stations.',
    apps: ['xotski'],
    permissions: [
      ...expand(CO_READ, READ),
      ...expand(['XkStock', 'XkEventStockStation'], WRITE),
      ...expand(['XkCompany', 'XkEvent', 'XkEventGroup', 'XkStation', 'XkDashboard'], READ),
    ],
  },
  {
    id: 'xk-events-editor',
    label: 'Events editor',
    description: 'Manage events, event groups and stations. Read-only on stock.',
    apps: ['xotski'],
    permissions: [
      ...expand(CO_READ, READ),
      ...expand(['XkEvent', 'XkEventGroup', 'XkStation'], WRITE),
      ...expand(['XkCompany', 'XkStock', 'XkEventStockStation', 'XkDashboard'], READ),
    ],
  },
  {
    id: 'xk-stations-operator',
    label: 'Stations operator',
    description: 'Manage stations and stock-station allocation. Read events and stock.',
    apps: ['xotski'],
    permissions: [
      ...expand(CO_READ, READ),
      ...expand(['XkStation', 'XkEventStockStation'], WRITE),
      ...expand(['XkCompany', 'XkEvent', 'XkEventGroup', 'XkStock', 'XkDashboard'], READ),
    ],
  },
  {
    id: 'xk-admin',
    label: 'Full admin (Xotski)',
    description: 'Full access on every Xotski entity.',
    apps: ['xotski'],
    permissions: [...expand(CO_READ, READ), ...expand(XK_ALL, WRITE)],
  },

  // ─── Xamplin ─────────────────────────────────────────────────────────────
  {
    id: 'xp-viewer',
    label: 'Viewer (read-only)',
    description: 'See everything in Xamplin. No edits.',
    apps: ['xamplin'],
    permissions: [...expand(CO_READ, READ), ...expand(XP_ALL, READ)],
  },
  {
    id: 'xp-project-manager',
    label: 'Project manager',
    description: 'Full control over projects and event groups. Read events and reporters.',
    apps: ['xamplin'],
    permissions: [
      ...expand(CO_READ, READ),
      ...expand(['XpProject', 'XpEventGroup'], WRITE_DEPLOY),
      ...expand(['XpEvents', 'XpStation', 'XpReporter'], READ),
    ],
  },
  {
    id: 'xp-event-coordinator',
    label: 'Event coordinator',
    description: 'Manage events and stations. Read projects and reporters.',
    apps: ['xamplin'],
    permissions: [
      ...expand(CO_READ, READ),
      ...expand(['XpEvents', 'XpStation'], WRITE),
      ...expand(['XpProject', 'XpEventGroup', 'XpReporter'], READ),
    ],
  },
  {
    id: 'xp-reporters-lead',
    label: 'Reporters lead',
    description: 'Full control over reporters. Read everything else.',
    apps: ['xamplin'],
    permissions: [
      ...expand(CO_READ, READ),
      ...expand(['XpReporter'], WRITE),
      ...expand(['XpProject', 'XpEventGroup', 'XpEvents', 'XpStation'], READ),
    ],
  },
  {
    id: 'xp-admin',
    label: 'Full admin (Xamplin)',
    description: 'Full access on every Xamplin entity.',
    apps: ['xamplin'],
    permissions: [...expand(CO_READ, READ), ...expand(XP_ALL, WRITE_DEPLOY)],
  },

  // ─── Notifications (core, available in any app) ─────────────────────────
  {
    id: 'notification-sender',
    label: 'Notification sender',
    description: 'Compose and send notifications to users in the company subtree.',
    apps: ['xotski', 'xamplin'],
    permissions: expand(['Notification'], ['show', 'list', 'edit', 'create', 'delete']),
  },
];

export function presetsForApp(app: string | null | undefined): PermissionPreset[] {
  if (!app) return PERMISSION_PRESETS;
  return PERMISSION_PRESETS.filter((p) => p.apps.includes(app));
}
